import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import DigtekNavbar from "@/components/digtek/DigtekNavbar";
import DigtekFooter from "@/components/digtek/DigtekFooter";
import { findInsight, insights } from "@/data/insights";

export default function InsightArticle() {
    const { slug } = useParams();
    const article = findInsight(slug);
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "instant" });
        if (article) document.title = `${article.title} — Aximo Studios`;
        return () => {
            document.title = "Aximo Studios — Full-Service Creative Agency";
        };
    }, [article]);

    useEffect(() => {
        const onScroll = () => {
            const docH = document.documentElement.scrollHeight - window.innerHeight;
            const pct = docH > 0 ? Math.min((window.scrollY / docH) * 100, 100) : 0;
            setProgress(pct);
        };
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    if (!article) return <Navigate to="/" replace />;

    const idx = insights.findIndex((a) => a.slug === slug);
    const next = insights[(idx + 1) % insights.length];

    return (
        <main
            data-testid="insight-article-page"
            className="digtek-page min-h-screen bg-ink text-bone"
        >
            <DigtekNavbar />

            {/* Reading progress bar */}
            <div
                aria-hidden
                data-testid="insight-progress"
                className="fixed top-0 left-0 right-0 h-0.5 bg-transparent z-50"
            >
                <div
                    className="h-full bg-ember transition-[width] duration-100 ease-linear"
                    style={{ width: `${progress}%` }}
                />
            </div>

            {/* Hero */}
            <section className="relative pt-32 pb-12 lg:pt-44 lg:pb-20 grain">
                <div className="max-w-[1100px] mx-auto px-6 lg:px-10">
                    <div className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-ember mb-8">
                        <span className="block w-8 h-px bg-ember" />
                        {article.category} · {article.date} ·{" "}
                        {article.readtime}
                    </div>
                    <h1
                        data-testid="insight-title"
                        className="font-display font-medium tracking-tightest text-bone text-5xl sm:text-6xl lg:text-7xl leading-[0.98]"
                    >
                        {article.title}
                    </h1>
                    <p className="mt-8 font-serif-i italic text-2xl lg:text-3xl text-bone-300 leading-snug max-w-[44ch]">
                        {article.excerpt}
                    </p>
                </div>
            </section>

            {/* Body */}
            <article className="pb-24">
                <div className="max-w-[760px] mx-auto px-6 lg:px-10 space-y-7 text-bone text-lg lg:text-xl leading-[1.7]">
                    {article.body.map((p, i) => (
                        <p
                            key={`${article.slug}-p-${i}`}
                            data-testid={`insight-paragraph-${i}`}
                            className="text-bone-300"
                        >
                            {p}
                        </p>
                    ))}
                    <div className="pt-8 border-t border-white/10 text-sm text-bone-500 flex items-center justify-between">
                        <span>
                            Filed in{" "}
                            <span className="text-ember">
                                {article.category}
                            </span>
                        </span>
                        <span>{article.readtime}</span>
                    </div>
                </div>
            </article>

            {/* Next + CTA */}
            <section className="py-20 lg:py-28 bg-ink-900 border-t border-white/10">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                    <Link
                        to={`/insights/${next.slug}`}
                        data-testid="insight-next"
                        className="group block"
                    >
                        <div className="text-[10px] uppercase tracking-[0.25em] text-bone-500 mb-3 flex items-center gap-2">
                            Next essay
                            <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                        </div>
                        <div className="font-display text-bone text-3xl lg:text-4xl tracking-tight group-hover:text-ember transition-colors">
                            {next.title}
                        </div>
                        <div className="text-bone-300 text-sm mt-2">
                            {next.category} · {next.date}
                        </div>
                    </Link>

                    <Link
                        to="/#contact"
                        data-testid="insight-cta"
                        className="lg:justify-self-end inline-flex items-center gap-2 btn-ember h-14 px-7 text-sm font-medium tracking-wide"
                    >
                        Talk to the studio
                        <ArrowUpRight className="w-4 h-4" />
                    </Link>
                </div>
            </section>

            <DigtekFooter />
        </main>
    );
}
